import { i18n } from "@lingui/core";
import { useLingui } from "@lingui/react";
import { useCallback, useState } from "react";
import { initialLocale, storeLocale, type Locale } from "./locale";

/**
 * Catalogs are the compiled `.ts` the Lingui CLI writes, imported rather than
 * fetched: `check-types` then sees them, and a missing catalog is a build error
 * instead of a blank console at runtime. Both are tiny, so both ship — a locale
 * switch that has to wait on a network round trip is a locale switch that
 * flickers through the English ids.
 */
import { messages as en } from "../locales/en/messages";
import { messages as mn } from "../locales/mn/messages";

i18n.load({ mn, en });
activate(initialLocale());

/**
 * `<html lang>` moves with the catalog, and it is set HERE rather than in the
 * shell because the sign-in page has no shell. A screen reader announcing
 * Mongolian text under an English language tag reads it letter by letter, and
 * the browser's own hyphen and line-break rules are language-dependent.
 */
function activate(locale: Locale): void {
  i18n.activate(locale);
  if (typeof document !== "undefined") document.documentElement.lang = locale;
}


/**
 * The console's one translation hook.
 *
 * `t` is the Lingui runtime API rather than the macro (see `lingui.config.ts`):
 * the English source string IS the message id, and `src/i18n/catalogs.test.ts`
 * refuses an `mn` catalog that leaves one untranslated — because with this API a
 * missing entry renders the English id on a Mongolian operator's screen and
 * nothing in the build would otherwise notice.
 */
export function useT() {
  const lingui = useLingui();
  // The name `i18n` is load-bearing: `lingui extract` finds messages by looking
  // for `i18n._("…")` calls, and a renamed wrapper (`t(…)`) is invisible to it —
  // the catalog would come out empty and every screen would render its English
  // id. The macro form that reads better needs a Babel or SWC transform in the
  // build, and `@vitejs/plugin-react` 6 runs Oxc (see lingui.config.ts).
  return { i18n: lingui.i18n, locale: lingui.i18n.locale as Locale };
}

/**
 * The locale switch in the command bar.
 *
 * `<html lang>` moves with it: a screen reader announcing Mongolian text with an
 * English language tag reads it letter by letter, and the browser's own hyphen
 * and line-break rules are language-dependent.
 */
export function useLocaleSwitch(): [Locale, (next: Locale) => void] {
  const { locale } = useT();
  const [, force] = useState(0);

  const set = useCallback((next: Locale) => {
    activate(next);
    storeLocale(next);
    // Lingui's own subscription is what re-renders the tree; this is what
    // re-renders the switch itself so its pressed state moves with it.
    force((n) => n + 1);
  }, []);

  return [locale, set];
}
